import { useEffect, useRef } from "react";
import axios from "axios";
import { useAuthStore } from "../stores/authStore";
import api from "../config/api";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const REFRESH_KEY = "dm_refresh_token";

/**
 * Proactively restores the user session on app startup when a refresh token
 * is present in localStorage but no access token is in memory.
 *
 * Without this, ProtectedRoute shows a permanent spinner because:
 *  - isAuthenticated starts as false
 *  - all queries are guarded by `enabled: isAuthenticated`, so no API call fires
 *  - no API call → no 401 → the interceptor's refresh logic never runs
 *
 * Call this once in App.jsx.
 */
export function useSessionRestore() {
  const attempted = useRef(false);
  const { isAuthenticated, login, logout, setAccessToken, setRefreshToken } =
    useAuthStore();

  useEffect(() => {
    // Skip if already authenticated or already attempted
    if (attempted.current || isAuthenticated) return;

    const storedToken = localStorage.getItem(REFRESH_KEY);
    if (!storedToken) return;

    attempted.current = true;

    (async () => {
      try {
        // 1. Exchange refresh token for a new access token
        const { data: refreshData } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken: storedToken },
        );
        const d = refreshData?.data || refreshData;
        const newAccess = d.accessToken;
        const newRefresh = d.refreshToken;

        // 2. Persist new tokens so subsequent API calls are authorised
        setAccessToken(newAccess);
        setRefreshToken(newRefresh);

        // 3. Fetch user profile (request interceptor will attach the new token)
        const meRes = await api.get("/auth/me");
        const user = meRes.data?.user || meRes.data;

        // 4. Fully restore the session — sets isAuthenticated: true
        login(user, newAccess, newRefresh);
      } catch {
        // Stale or invalid refresh token — clear it so ProtectedRoute redirects
        logout();
      }
    })();
  // Run once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
